import React from 'react';

import { AtOutline, Calendar2Outline, CheckMark, Tag, TvOutline } from '@lyearn/icons';
import {
  Briefcase,
  Calendar2,
  Department,
  GridOutline,
  PeopleOutline,
  PersonOutline,
} from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Stack, Typography, TypographyVariants } from '@lyearn/ui';
import getTagIcon from '@/modules/corporate/modules/tags/views/TagsManager/utils/getTagIcon';
import { FilterPropertyTypeEnum } from '@/types/schema';
import { IconType } from '@/utils/icon';

import { iconStyles } from './styles';
import { SystemProperty, TagAndPropertyOption } from './types';

const CUSTOM_PROPERTIES_ICON = {
  [SystemProperty.OrgRoleId]: Briefcase,
  [SystemProperty.DepartmentId]: Department,
  [SystemProperty.ManagerId]: PersonOutline,
  [SystemProperty.IndirectManagerId]: PeopleOutline,
  [SystemProperty.CreatedById]: PeopleOutline,
  [SystemProperty.CreatedAt]: Calendar2,
  [SystemProperty.Type]: GridOutline,
  [SystemProperty.Email]: AtOutline,
  [SystemProperty.EventType]: TvOutline,
  [SystemProperty.EventDate]: Calendar2Outline,
};

const OPTIONS_STYLE_OVERRIDES = {
  medium: {
    name: {
      variant: 'body-short-02' as TypographyVariants,
    },
    checkMarkIcon: {
      className: 'w-20 h-20',
    },
  },
  large: {
    name: {
      variant: 'body-short-01' as TypographyVariants,
    },
    checkMarkIcon: {
      className: 'w-24 h-24',
    },
  },
};

export const getCustomPropertyIcon = ({
  id,
  size = 20,
  isSelected = false,
}: {
  id: SystemProperty;
  size?: number;
  isSelected?: boolean;
}) => {
  const Icon = CUSTOM_PROPERTIES_ICON[id];

  if (!Icon) {
    return null;
  }

  return (
    <Icon
      className={`w-${size} h-${size}`}
      fillColorToken={isSelected ? 'icon-active' : 'icon-secondary'}
    />
  );
};

const renderIcon = (val: TagAndPropertyOption, isSelected: boolean) => {
  if (val.type === FilterPropertyTypeEnum.Field) {
    return getCustomPropertyIcon({ id: val.id as SystemProperty, isSelected });
  }
  if (val.tag) {
    return getTagIcon(val.tag);
  }
  return null;
};

export const renderAutoCompleteOption =
  (selectedValue?: TagAndPropertyOption, size?: 'medium' | 'large') =>
  (val: TagAndPropertyOption) => {
    const classes = OPTIONS_STYLE_OVERRIDES[size!];
    const isSelected = selectedValue?.id === val.id;

    return (
      <Stack alignItems="center" className="mb-2 w-full py-10 px-12" justifyContent="between">
        <Stack alignItems="center" className="flex-0 truncate">
          <Stack alignItems="center" className="flex-0">
            {renderIcon(val, isSelected)}
          </Stack>
          <Typography
            className="truncate ms-12"
            color={isSelected ? 'text-text-primary' : 'text-text-secondary'}
            variant={classes.name.variant}>
            {val.name}
          </Typography>
        </Stack>
        {isSelected ? (
          <CheckMark
            className={cn('flex-shrink-0 ms-12', classes.checkMarkIcon.className)}
            fillColorToken="icon-active"
          />
        ) : null}
      </Stack>
    );
  };

export const renderStartAdornment =
  (
    selectedValue?: TagAndPropertyOption,
    PlaceholderIcon?: IconType,
    showPlaceholderIcon?: boolean,
  ) =>
  () => {
    if (selectedValue) {
      return (
        <Stack alignItems="center" className={iconStyles}>
          {renderIcon(selectedValue, true)}
        </Stack>
      );
    }

    if (!showPlaceholderIcon) {
      return <></>;
    }

    if (PlaceholderIcon) {
      return <PlaceholderIcon className="h-20 w-20" fillColorToken="icon-placeholder" />;
    }

    return <Tag className="h-20 w-20" fillColorToken="icon-secondary" />;
  };
